import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { cn } from '../cn.js';
const FOCUSABLE = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
export function Dialog({ title, header, children, footer, onClose, dismissible = true, initialFocus, className, 'data-testid': testId, }) {
    const dialogRef = useRef(null);
    const titleId = useRef(`dialog-title-${Math.random().toString(36).slice(2, 9)}`).current;
    // The element focused before the dialog opened. Focus goes back to it on
    // close, so a keyboard operator resumes where they left off rather than at
    // the top of the document.
    const previouslyFocusedRef = useRef(null);
    useEffect(() => {
        const active = typeof document !== 'undefined' ? document.activeElement : null;
        if (active instanceof HTMLElement)
            previouslyFocusedRef.current = active;
        return () => {
            const target = previouslyFocusedRef.current;
            previouslyFocusedRef.current = null;
            // Defer so React has finished unmounting before focus moves.
            if (target?.focus)
                queueMicrotask(() => target.focus());
        };
    }, []);
    useEffect(() => {
        const node = dialogRef.current;
        if (!node)
            return;
        const target = (initialFocus ? node.querySelector(initialFocus) : null) ??
            node.querySelector(FOCUSABLE) ??
            node;
        target.focus();
    }, [initialFocus]);
    // Listens on the document, not on the overlay. A handler bound to the
    // dialog subtree only fires while focus is already inside it, which is
    // precisely the case that does not need fixing — the one that does is focus
    // having drifted out to the page behind.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && dismissible) {
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key !== 'Tab')
                return;
            const node = dialogRef.current;
            if (!node)
                return;
            const focusable = Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');
            if (focusable.length === 0) {
                e.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            if (!active || !node.contains(active)) {
                e.preventDefault();
                first.focus();
                return;
            }
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        // window, not document: a key event dispatched on either reaches a window
        // listener, so this catches both real browser input and tests that fire at
        // whichever target they picked.
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [dismissible, onClose]);
    return (_jsx("div", { role: "presentation", className: "fixed inset-0 z-50 flex items-center justify-center bg-on-surface/40 p-4", onClick: (e) => {
            if (e.target === e.currentTarget && dismissible)
                onClose();
        }, children: _jsxs("div", { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, tabIndex: -1, "data-testid": testId, className: cn('flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl', 'border border-outline-variant bg-surface-container-lowest p-6 shadow-lg outline-none', className), children: [header ?? (_jsx("h2", { id: titleId, className: "font-headline-sm text-headline-sm text-on-surface", children: title })), header ? (_jsx("span", { id: titleId, className: "sr-only", children: title })) : null, children, footer] }) }));
}
//# sourceMappingURL=Dialog.js.map